import { memo, useCallback, useMemo, useState } from "react";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { buildTemplateSendSummary } from "../helpers/buildTemplateSendSummary";
import type {
  CrmPatientSummary,
  FluxZapCommunicationRecord,
  TemplateSendAggregateRow,
} from "../types";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import { CommunicationSendDetailDialog } from "./CommunicationsDetailDialogs";
import CommunicationsTemplateSendRankTable from "./CommunicationsTemplateSendRankTable";
import CommunicationsTemplateSendRows, {
  type TemplateSendDetailPayload,
} from "./CommunicationsTemplateSendRows";
import { communicationsSubsectionIconBoxSx } from "./communicationsSubsectionStyles";

export interface CommunicationsModelsFocusPanelProps {
  templateRows: TemplateSendAggregateRow[];
  selectedTemplateCode: string | null;
  onSelectTemplateCode: (code: string | null) => void;
  selectedTemplateMessages: FluxZapCommunicationRecord[];
  vetApp?: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  resolvePatientForMessage: (
    msg: FluxZapCommunicationRecord,
  ) => CrmPatientSummary | undefined;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
}

/**
 * Foco em modelos: KPIs em cards, ranking em tabela e detalhe vinculado à seleção.
 */
const CommunicationsModelsFocusPanel = memo(
  ({
    templateRows,
    selectedTemplateCode,
    onSelectTemplateCode,
    selectedTemplateMessages,
    vetApp = false,
    petNamesByTutorLogicalId,
    resolvePatientForMessage,
    onOpenSendMessage,
  }: CommunicationsModelsFocusPanelProps) => {
    const [sendDetail, setSendDetail] =
      useState<TemplateSendDetailPayload | null>(null);

    const summary = useMemo(
      () => buildTemplateSendSummary(templateRows),
      [templateRows],
    );

    const selectedTemplateLabel = useMemo(
      () =>
        templateRows.find((r) => r.templateCode === selectedTemplateCode)
          ?.templateLabel ?? null,
      [templateRows, selectedTemplateCode],
    );

    const closeDetail = useCallback(() => setSendDetail(null), []);

    if (templateRows.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          Nenhum envio registrado ainda.
        </Typography>
      );
    }

    return (
      <Stack spacing={2}>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
                <SmsOutlinedIcon fontSize="small" />
              </Paper>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Total de envios
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {summary.totalSends.toLocaleString("pt-BR")} envios
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
                <ViewModuleOutlinedIcon fontSize="small" />
              </Paper>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary">
                  Modelos utilizados
                </Typography>
                <Typography variant="subtitle1" fontWeight={700}>
                  {summary.templateCount.toLocaleString("pt-BR")} modelos
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              height: "100%",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="flex-start">
              <Paper variant="outlined" sx={communicationsSubsectionIconBoxSx}>
                <EmojiEventsOutlinedIcon fontSize="small" />
              </Paper>
              <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Modelo líder
                </Typography>
                <Tooltip title={summary.topTemplateLabel ?? ""}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {summary.topTemplateLabel ?? "—"}
                  </Typography>
                </Tooltip>
                {summary.topTemplateLabel ? (
                  <Typography variant="caption" color="text.secondary">
                    {summary.topTemplateSharePercent}% dos envios
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Paper>
        </Box>

        <CommunicationsTemplateSendRankTable
          templateRows={templateRows}
          totalSends={summary.totalSends}
          selectedTemplateCode={selectedTemplateCode}
          onSelectTemplateCode={onSelectTemplateCode}
        />

        {selectedTemplateCode ? (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: "background.paper",
              borderColor: "primary.main",
              borderLeft: "4px solid",
              borderLeftColor: "primary.main",
              mt: 0.5,
            }}
          >
            <CommunicationsTemplateSendRows
              selectedTemplateMessages={selectedTemplateMessages}
              selectedTemplateCode={selectedTemplateCode}
              selectedTemplateLabel={selectedTemplateLabel}
              selectedTemplateSendCount={selectedTemplateMessages.length}
              vetApp={vetApp}
              petNamesByTutorLogicalId={petNamesByTutorLogicalId}
              resolvePatientForMessage={resolvePatientForMessage}
              onClearSelection={() => onSelectTemplateCode(null)}
              onOpenSendDetail={setSendDetail}
              onOpenSendMessage={onOpenSendMessage}
            />
          </Paper>
        ) : null}

        <CommunicationSendDetailDialog
          open={sendDetail != null}
          onClose={closeDetail}
          messages={sendDetail?.messages ?? []}
          patient={sendDetail?.patient}
          vetApp={vetApp}
          petNamesByTutorLogicalId={petNamesByTutorLogicalId}
        />
      </Stack>
    );
  },
);

CommunicationsModelsFocusPanel.displayName = "CommunicationsModelsFocusPanel";

export default CommunicationsModelsFocusPanel;
